import React, { useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import { IconMinus, IconPlus } from '@tabler/icons-react';

/**
 * Visible rows in depth-first order; children included only when parent id is expanded.
 * `ancestorIsLastPrefix[k]` = whether the ancestor at depth k+1 on this path was the last child among its siblings (kept on each row for parity with the visible flat list).
 * Through-lines in column k draw for every non-elbow column; `vEnd` uses `nextContinuesSpine` (+ last flat row / last top-level org under AO) so spines stop at the last visible descendant, including under the final org below “All Organizations”.
 * `isLastChild` drives the elbow for this row.
 */
export function flattenVisibleOrgTree(nodes, expandedSet) {
  if (!Array.isArray(nodes) || !nodes.length) return [];
  const out = [];
  /** `parentFlatIndex` is set from the walk (not from id lookup) so duplicate `item.id` values cannot attach to the wrong parent — critical for search + connector bounds. */
  const walk = (items, depth, parentId, ancestorIsLastPrefix, parentFlatIndex) => {
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const isLastChild = index === items.length - 1;
      const idStr = String(item.treeRowKey != null && item.treeRowKey !== '' ? item.treeRowKey : item.id);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const parentKey = parentId == null || parentId === undefined ? null : String(parentId);
      const myIndex = out.length;
      out.push({
        item,
        depth,
        parentId: parentKey,
        hasChildren,
        idStr,
        ancestorIsLastPrefix,
        isLastChild,
        parentFlatIndex
      });
      if (hasChildren && expandedSet.has(idStr)) {
        const nextPrefix = depth === 0 ? [] : [...ancestorIsLastPrefix, isLastChild];
        walk(item.children, depth + 1, idStr, nextPrefix, myIndex);
      }
    }
  };
  walk(nodes, 0, null, [], -1);
  return out;
}

/**
 * Re-derive isLastChild + ancestorIsLastPrefix from the **visible** flat list only.
 * Fixes guide lines after search/filter (lines no longer run past the last visible sibling)
 * and keeps ancestor spines aligned with what’s actually rendered.
 */
function recomputeVisibleConnectorFlags(rows) {
  if (!rows.length) {
    return { rows, lastDepth1ChildId: null };
  }
  rows.forEach((r, i) => {
    r.flatIndex = i;
  });
  /** Group by parent *row index* (not parent id) so duplicate ids under different parents stay isolated. */
  const byParent = new Map();
  for (const row of rows) {
    const pkey = row.parentFlatIndex < 0 ? '__root__' : String(row.parentFlatIndex);
    if (!byParent.has(pkey)) byParent.set(pkey, []);
    byParent.get(pkey).push(row);
  }
  for (const [, group] of byParent) {
    group.forEach((r, i) => {
      r.isLastChild = i === group.length - 1;
    });
  }
  /** True iff this node has at least one row in the flat list as its child (differs from `item.children` after search/filter). */
  for (const row of rows) {
    row.hasVisibleChildren = (byParent.get(String(row.flatIndex)) || []).length > 0;
  }

  let lastDepth1ChildId = null;
  for (const row of rows) {
    if (row.depth === 1) lastDepth1ChildId = row.idStr;
  }

  for (const row of rows) {
    /**
     * Column k = ancestor at depth k+1 (shallow → deep). Walk up via parentFlatIndex; skip depth-0 root.
     * Use each ancestor row’s recomputed `isLastChild` (not a map keyed by id) so duplicate ids don’t corrupt flags.
     */
    const ancestorRows = [];
    let cur = row;
    for (;;) {
      const pIdx = cur.parentFlatIndex;
      if (pIdx == null || pIdx < 0) break;
      const pr = rows[pIdx];
      if (!pr || pr.depth === 0) break;
      ancestorRows.push(pr);
      cur = pr;
    }
    ancestorRows.reverse();
    const expectedPrefixLen = Math.max(0, row.depth - 1);
    const trimmed = ancestorRows.map((a) => a.isLastChild);
    while (trimmed.length < expectedPrefixLen) {
      trimmed.push(true);
    }
    row.ancestorIsLastPrefix = trimmed.slice(0, expectedPrefixLen);
  }
  return { rows, lastDepth1ChildId };
}

/** Walk `parentFlatIndex` until depth matches `targetDepth` (inclusive of that ancestor row). */
function getAncestorRowAtDepth(row, targetDepth, flatRows) {
  if (!row || targetDepth < 0 || !flatRows?.length) return null;
  let current = row;
  while (current.depth > targetDepth) {
    const pIdx = current.parentFlatIndex;
    if (pIdx == null || pIdx < 0) return null;
    const next = flatRows[pIdx];
    if (!next) return null;
    current = next;
  }
  return current.depth === targetDepth ? current : null;
}

/** True if `row` is a strict descendant of the node identified by `ancestorId` (uses preorder parent indices). */
function rowIsStrictDescendantOf(row, ancestorRowIdStr, flatRows) {
  if (!row || ancestorRowIdStr == null || ancestorRowIdStr === undefined || !flatRows?.length) return false;
  const target = String(ancestorRowIdStr);
  let pIdx = row.parentFlatIndex;
  while (pIdx != null && pIdx >= 0) {
    const pr = flatRows[pIdx];
    if (!pr) return false;
    if (String(pr.idStr) === target) return true;
    pIdx = pr.parentFlatIndex;
  }
  return false;
}

/** Same depth and same parent → adjacent branch columns must keep drawing through to the next sibling (e.g. SSIDDELETE → Location_9, or last Loc under Org A → Org B). */
function isTreeSiblingRow(rowA, rowB) {
  if (!rowA || !rowB) return false;
  return rowA.depth === rowB.depth && String(rowA.parentId ?? '__root__') === String(rowB.parentId ?? '__root__');
}

/**
 * For each preorder index i, last index j ≥ i still in the subtree rooted at row i
 * (next row at j+1 has depth ≤ row[i].depth, or j is the list end). O(n) — used to
 * cap verticals after search so spines never run past the last *visible* descendant.
 */
function computePreorderSubtreeEndIndex(rows) {
  const n = rows.length;
  if (!n) return [];
  const sl = new Array(n);
  sl[n - 1] = n - 1;
  for (let i = n - 2; i >= 0; i--) {
    const d = rows[i].depth;
    let k = i + 1;
    if (k >= n || rows[k].depth <= d) {
      sl[i] = i;
      continue;
    }
    let j = sl[k];
    k = j + 1;
    while (k < n && rows[k].depth > d) {
      j = sl[k];
      k = j + 1;
    }
    sl[i] = j;
  }
  return sl;
}

const ROW_HEIGHT = 46;
/** Gutter width per tree level; wider = more space from spine to node row (e.g. under “All Organizations”). */
const CONNECTOR_COL_WIDTH = 20;
/** Spine x inside each gutter column (column center; keeps SVG strokes inside the 20px strip). */
const CONNECTOR_SPINE_X = Math.floor(CONNECTOR_COL_WIDTH / 2);
/** Expand/collapse (+ / −); keep in sync with elbow horizontal extension. */
const TREE_ICON_COL_WIDTH = 22;
/** Visible +/- box (fits 14px glyphs + 0.5px border); used for connector horizontal reach. */
const TREE_EXPAND_ICON_BOX_PX = 13;
/** Leaf marker `CircleIcon` width. */
const TREE_LEAF_DOT_PX = 8;
/** IconButton uses `ml: -1px`; horizontal must reach past gutter into the control. */
const ICON_BTN_MARGIN_LEFT_PX = 1;

/** Single parse per tree render (avoids JSON.parse × visible rows). */
function readSelectedTypeStorage() {
  try {
    const raw = localStorage.getItem('selectedType');
    if (!raw) return { hasStored: false, type: undefined };
    const parsed = JSON.parse(raw);
    return { hasStored: true, type: parsed?.type };
  } catch {
    return { hasStored: true, type: undefined };
  }
}

function selectionHighlightBg(theme, isSelected, item, calledFrom, selectedTypeStorage) {
  if (!isSelected) return 'transparent';
  if (item.type === 'All Organizations') return 'transparent';
  const dark = theme.palette.mode === 'dark';
  const hl = dark ? theme.palette.secondary.main : theme.palette.bluegrey.light;
  if (selectedTypeStorage.hasStored) {
    if (selectedTypeStorage.type === 'All Organizations' && calledFrom !== 'addDeviceAllOrg') return 'transparent';
    return hl;
  }
  if (calledFrom === 'addDeviceAllOrg') return hl;
  return 'transparent';
}

/**
 * Integer spine x per gutter column (same local x in every column so global positions step by `CONNECTOR_COL_WIDTH`).
 */
const SPINE_SVG_X = CONNECTOR_SPINE_X;
/**
 * Row midline = vertical center of a `TREE_ICON_COL_WIDTH` control with `alignItems: center`
 * (ROW_HEIGHT 46, icon column 22 → center at 23).
 */
const JOINT_Y = ROW_HEIGHT / 2;
/** Horizontal branches use `lineColor`; vertical spines use `lineColorVert` + `GUIDE_VERT_STROKE_WIDTH`. */
const GUIDE_HORIZ_STROKE_WIDTH = 0.2;
const GUIDE_VERT_STROKE_WIDTH = 0.2;
/** Pull T‑junction horizontals slightly past the spine so two `<path>`s read as one joint. */
const T_HORIZ_OVERLAP = 1.25;
/**
 * Seam bleed between rows. Per column: if the row above painted this gutter (`k < prevRowDepth`),
 * start at +O to avoid double-stroke; if this column is new (deeper indent), start at −O so the
 * spine bleeds into the parent row (no line there yet). Rows use integer `top` to avoid subpixel gaps.
 */
const VERT_OVERLAP_PX = 3;
/** Top of the bordered +/- inner box inside the expand `IconButton` (matches row layout below). */
const TREE_EXPAND_INNER_TOP_Y = (ROW_HEIGHT - TREE_ICON_COL_WIDTH) / 2 + (TREE_ICON_COL_WIDTH - TREE_EXPAND_ICON_BOX_PX) / 2;
const TREE_EXPAND_ICON_BOTTOM_FROM_ROW_TOP = TREE_EXPAND_INNER_TOP_Y + TREE_EXPAND_ICON_BOX_PX;
/**
 * Virtual rows stack with no gap; the first child row’s spine must reach up into the parent row to the
 * bottom of the parent’s +/- box (3px bleed alone leaves a visible gap under the collapse icon).
 */
const PARENT_CHILD_CONNECTOR_BLEED_PX = ROW_HEIGHT - TREE_EXPAND_ICON_BOTTOM_FROM_ROW_TOP;

/**
 * Guide lines as SVG strokes only — no filled `Box` rects (those read as a tinted gutter,
 * especially when `lineColor` is a light grey like `bluegrey100` in light mode).
 */
function OrgTreeConnectorGutters({
  depth,
  isLastChild,
  lineColor,
  lineColorVert,
  hasChildren,
  /** From flat list: any rendered child row; when false but `hasChildren`, connectors must not pretend children are shown. */
  hasVisibleChildren,
  nodeType,
  /** With `isLastChild`, when true the elbow is a T so the spine continues below the icon to children. */
  isExpanded = false,
  /** Flat-list depth of the logical previous row (`flatRows[index - 1]`); used for spine seams (must not depend on virtualizer mount — search/long lists often unmount the row above). */
  prevRowDepth,
  /** Current flat row (for ancestor lookup). */
  flatRow,
  /** Full visible flat list (preorder); used with `parentFlatIndex` so search/filtered trees don’t mis-resolve ancestors via duplicate ids. */
  flatRows,
  /** Last depth-1 row id under All Organizations in preorder (stops AO spine extending past final top-level branch). */
  lastDepth1ChildId,
  /** `subtreeEndIndex[i]` = last flat index in preorder subtree rooted at row i (visible list only). */
  subtreeEndIndex,
  /** Index of this row in the full flat list + list length — last row must not extend verticals into empty space (search/filter can skew `isLastChild` when ids collide). */
  flatRowIndex,
  flatRowCount
}) {
  if (depth <= 0) return null;
  /** Always derive from `flatRows` + index so the last row never sees a bogus “next” (fixes spine past list end). */
  const nextLogicalRow =
    Array.isArray(flatRows) &&
    flatRows.length > 0 &&
    typeof flatRowIndex === 'number' &&
    typeof flatRowCount === 'number' &&
    flatRowCount > 0 &&
    flatRowIndex + 1 < flatRowCount
      ? flatRows[flatRowIndex + 1]
      : undefined;

  /** Left edge of expand/leaf graphic inside the last gutter column’s coordinate system. */
  const iconBtnLeftLocal = CONNECTOR_COL_WIDTH - ICON_BTN_MARGIN_LEFT_PX;
  const graphicInset = hasChildren ? (TREE_ICON_COL_WIDTH - TREE_EXPAND_ICON_BOX_PX) / 2 : (TREE_ICON_COL_WIDTH - TREE_LEAF_DOT_PX) / 2;
  const graphicLeftLocal = iconBtnLeftLocal + graphicInset;
  const HORIZ_EXTEND_PX = 2;
  const ORG_NODE_HORIZ_EXTRA_PX = 2;
  const orgExtra = nodeType === 'Org' || nodeType === 'All Organizations' ? ORG_NODE_HORIZ_EXTRA_PX : 0;
  const horizEndX = hasChildren ? graphicLeftLocal : graphicLeftLocal + 4 + HORIZ_EXTEND_PX + orgExtra;

  const sx = SPINE_SVG_X;
  /** Match +/- box vertical center (same flex geometry as `VirtualOrgTreeRow` icon column). */
  const jy = hasChildren ? TREE_EXPAND_INNER_TOP_Y + TREE_EXPAND_ICON_BOX_PX / 2 : JOINT_Y;
  /** Shift Y into SVG inner coords so paths stay inside width×height (root SVG often clips outside bounds). */
  const svgY = (y) => y + VERT_OVERLAP_PX;
  const svgH = ROW_HEIGHT + 2 * VERT_OVERLAP_PX;
  const strokeCommon = { fill: 'none', shapeRendering: 'geometricPrecision' };
  const strokeVert = { ...strokeCommon, stroke: lineColorVert, strokeWidth: GUIDE_VERT_STROKE_WIDTH };
  const strokeHoriz = { ...strokeCommon, stroke: lineColor, strokeWidth: GUIDE_HORIZ_STROKE_WIDTH };

  const vBot = ROW_HEIGHT + VERT_OVERLAP_PX;
  const isLastRenderedFlatRow =
    typeof flatRowIndex === 'number' && typeof flatRowCount === 'number' && flatRowCount > 0 && flatRowIndex === flatRowCount - 1;

  /** Same-column seam vs bleed into row above where that column did not paint (first row of a deeper indent). */
  const vertTopY = (k) => {
    if (prevRowDepth == null) return -VERT_OVERLAP_PX;
    if (k < prevRowDepth) return VERT_OVERLAP_PX;
    const isElbowCol = k === depth - 1;
    const isDirectChildOfRowAbove = prevRowDepth === depth - 1;
    if (isElbowCol && isDirectChildOfRowAbove) {
      return -PARENT_CHILD_CONNECTOR_BLEED_PX;
    }
    return -VERT_OVERLAP_PX;
  };

  const cols = [];
  for (let k = 0; k < depth; k++) {
    const isElbow = k === depth - 1;
    // Draw the spine for every ancestor column; segment bottom (`vEnd`) is capped by `nextContinuesSpine`
    // so the line from “All Organizations” (via column 0 under each org) ends at the last visible descendant,
    // not in padding. Omitting through-lines when the ancestor was “last sibling” used to hide overrun but
    // removed the entire spine under the last top-level org.
    const showVertThrough = !isElbow;
    const vTop = vertTopY(k);

    const segments = [];
    if (showVertThrough) {
      // Column k tracks ancestor N at depth k+1. Cap using preorder subtree bounds so filtered/search
      // trees never extend the spine past N’s last *visible* descendant (parentId / descendant heuristics alone can leak).
      const ancRow = flatRow && flatRows?.length ? getAncestorRowAtDepth(flatRow, k + 1, flatRows) : null;
      const ancIdx = ancRow?.flatIndex;
      const ancEnd =
        ancIdx != null && ancIdx >= 0 && subtreeEndIndex && subtreeEndIndex.length > ancIdx ? subtreeEndIndex[ancIdx] : flatRowCount - 1;
      const nextIdx = typeof flatRowIndex === 'number' && flatRowCount > 0 ? flatRowIndex + 1 : -1;
      const ancIsLastTopLevelUnderAo = ancRow?.depth === 1 && lastDepth1ChildId != null && ancRow.idStr === lastDepth1ChildId;
      const nextStillUnderAncSubtree =
        nextLogicalRow != null && ancRow != null && ancIdx != null && ancIdx >= 0 && nextIdx >= 0 && nextIdx <= ancEnd && nextIdx > ancIdx;
      const atLastRowOfAncSubtree = ancIdx != null && ancIdx >= 0 && typeof flatRowIndex === 'number' && flatRowIndex === ancEnd;
      const siblingBridge =
        nextLogicalRow != null &&
        ancRow != null &&
        atLastRowOfAncSubtree &&
        !ancIsLastTopLevelUnderAo &&
        isTreeSiblingRow(nextLogicalRow, ancRow);
      const nextContinuesSpine = nextLogicalRow != null && ancRow != null && (nextStillUnderAncSubtree || siblingBridge);
      const subtreeEndsHere = !nextContinuesSpine || isLastRenderedFlatRow;
      const vEnd = subtreeEndsHere ? jy : vBot;
      segments.push(<path key="v-through" d={`M ${sx} ${svgY(vTop)} L ${sx} ${svgY(vEnd)}`} {...strokeVert} />);
    }
    if (isElbow) {
      /**
       * Last sibling is normally an “L” (no spine below the row). If it’s expanded, children need a trunk —
       * use the same T geometry as a middle sibling so the vertical runs through the full row below the icon.
       * When expanded but the next visible row is not under this node (filtered tree / search), do not draw the
       * stem below the horizontal — otherwise the “T” tail runs into the next branch or empty padding.
       */
      const elbowAsT = !isLastChild || (Boolean(hasVisibleChildren) && isExpanded);
      if (elbowAsT) {
        const hx0 = Math.max(0, sx - T_HORIZ_OVERLAP);
        const nextIsDirectChild =
          nextLogicalRow != null && flatRow?.idStr != null && String(nextLogicalRow.parentId) === String(flatRow.idStr);
        const selfEnd =
          typeof flatRowIndex === 'number' && subtreeEndIndex && subtreeEndIndex.length > flatRowIndex
            ? subtreeEndIndex[flatRowIndex]
            : typeof flatRowCount === 'number'
              ? flatRowCount - 1
              : 0;
        const nextIdxElbow = typeof flatRowIndex === 'number' && typeof flatRowCount === 'number' ? flatRowIndex + 1 : -1;
        const nextInsideSelfSubtree = nextLogicalRow != null && nextIdxElbow >= 0 && nextIdxElbow <= selfEnd;
        const needTrunkBelowJy =
          !isLastChild ||
          (Boolean(hasVisibleChildren && isExpanded) &&
            nextLogicalRow != null &&
            nextInsideSelfSubtree &&
            flatRow?.item?.id != null &&
            (nextIsDirectChild || (flatRows?.length > 0 && rowIsStrictDescendantOf(nextLogicalRow, flatRow.idStr, flatRows))));
        const elbowVertEnd = needTrunkBelowJy && !isLastRenderedFlatRow ? vBot : jy;
        segments.push(<path key="v-full" id="t1" d={`M ${sx} ${svgY(vTop)} L ${sx} ${svgY(elbowVertEnd)}`} {...strokeVert} />);
        segments.push(
          <path
            key="h-arm"
            id="t2"
            d={`M ${hx0 + 1} ${svgY(jy)} L ${horizEndX - (!hasChildren ? 6 : 0)} ${svgY(jy)}`}
            {...strokeHoriz}
            strokeLinecap="butt"
          />
        );
      } else {
        segments.push(<path key="elbow-l-v" d={`M ${sx} ${svgY(vTop)} L ${sx} ${svgY(jy)}`} {...strokeVert} />);
        segments.push(
          <path
            key="elbow-l-h"
            d={`M ${sx} ${svgY(jy)} L ${horizEndX - (!hasChildren ? 5 : 0)} ${svgY(jy)}`}
            {...strokeHoriz}
            strokeLinecap="butt"
          />
        );
      }
    }

    const svgW = isElbow ? Math.ceil(horizEndX + 2) : CONNECTOR_COL_WIDTH;

    cols.push(
      <Box
        key={k}
        className="org-tree-connector-col"
        sx={{
          width: CONNECTOR_COL_WIDTH,
          height: ROW_HEIGHT,
          flexShrink: 0,
          position: 'relative',
          overflow: 'visible',
          bgcolor: 'transparent'
        }}
      >
        <Box
          component="svg"
          width={svgW}
          height={svgH}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          overflow="visible"
          sx={{
            position: 'absolute',
            left: 0,
            top: -VERT_OVERLAP_PX,
            display: 'block',
            pointerEvents: 'none',
            bgcolor: 'transparent',
            overflow: 'visible'
          }}
          aria-hidden
        >
          {segments}
        </Box>
      </Box>
    );
  }
  return (
    <Box
      className="org-tree-connector-gutters"
      aria-hidden
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
        height: ROW_HEIGHT,
        overflow: 'visible',
        bgcolor: 'transparent'
      }}
    >
      {cols}
    </Box>
  );
}

const VirtualOrgTreeRow = React.memo(function VirtualOrgTreeRow({
  row,
  isExpanded,
  toggleExpandedItem,
  handleNodeClick,
  calledFrom,
  selectedNodeId,
  selectedValue,
  selectedType,
  selectedTypeStorage,
  isLastOrgTreeRow,
  prevRowDepth,
  flatRows,
  lastDepth1ChildId,
  subtreeEndIndex,
  flatRowIndex,
  flatRowCount
}) {
  const theme = useTheme();
  const { item, depth, parentId, hasChildren, hasVisibleChildren, isLastChild } = row;
  const childCount = item.children?.length ?? 0;
  const guideLineColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900];
  const guideLineVertColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900];

  const useIdMatching =
    (calledFrom === 'addDeviceAllOrg' && selectedNodeId && selectedNodeId !== 'Select Location') ||
    (calledFrom !== 'addDeviceAllOrg' && selectedNodeId && selectedNodeId !== 'Organization-wide');
  const isSelected = useIdMatching
    ? item.id == selectedNodeId
    : selectedValue === item.label && (!selectedType || selectedType === item.type);

  const showCount = true;
  const labelText = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: showCount && childCount > 0 ? '4px' : '2px' }}>
      {item.icon}
      {item.label.length > 22 ? (
        <Typography
          variant="body1"
          component={showCount && childCount > 0 ? 'span' : 'p'}
          title={item.label}
          sx={{
            fontWeight: 400,
            whiteSpace: 'pre',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '190px'
          }}
        >
          {item.label}
        </Typography>
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            whiteSpace: 'pre'
          }}
        >
          {item.label}
        </Typography>
      )}
    </Box>
  );

  const onExpandClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      toggleExpandedItem(row.idStr);
    },
    [toggleExpandedItem, row.idStr]
  );

  const onLabelClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleNodeClick(item.id, item.label, calledFrom, item.type, item, parentId);
    },
    [handleNodeClick, item, calledFrom, parentId]
  );

  const selectedBg = selectionHighlightBg(theme, isSelected, item, calledFrom, selectedTypeStorage);
  /** Scoped to the +/- box only (Tabler inherits `currentColor`); keep labels on `text.primary`. */
  const expandGlyphColor = theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.common.black;
  const isLastOrgRow = isLastOrgTreeRow && item.type === 'Org';
  return (
    <Box
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        boxSizing: 'border-box',
        height: ROW_HEIGHT,
        minHeight: ROW_HEIGHT,
        maxHeight: ROW_HEIGHT,
        overflow: 'visible',
        bgcolor: 'transparent'
      }}
    >
      <OrgTreeConnectorGutters
        depth={depth}
        isLastChild={isLastChild}
        lineColor={guideLineColor}
        lineColorVert={guideLineVertColor}
        hasChildren={hasChildren}
        hasVisibleChildren={hasVisibleChildren}
        nodeType={item.type}
        prevRowDepth={prevRowDepth}
        isExpanded={Boolean(hasChildren && isExpanded)}
        flatRow={row}
        flatRows={flatRows}
        lastDepth1ChildId={lastDepth1ChildId}
        subtreeEndIndex={subtreeEndIndex}
        flatRowIndex={flatRowIndex}
        flatRowCount={flatRowCount}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          height: ROW_HEIGHT,
          bgcolor: 'transparent'
        }}
      >
        {hasChildren ? (
          <IconButton
            type="button"
            disableRipple
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            onClick={onExpandClick}
            sx={{
              p: 0,
              width: TREE_ICON_COL_WIDTH,
              height: TREE_ICON_COL_WIDTH,
              flexShrink: 0,
              ml: '-1px',
              borderRadius: '4px',
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            id={`org-dropdown-tree-expand-${row.idStr}`}
          >
            <Box
              sx={{
                width: TREE_EXPAND_ICON_BOX_PX,
                height: TREE_EXPAND_ICON_BOX_PX,
                fontSize: '11px',
                lineHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                border: '0.5px solid',
                borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.800',
                borderRadius: '4px',
                /** Transparent so tree SVG strokes (drawn under this node in DOM order) show through to the row. */
                bgcolor: 'transparent',
                color: expandGlyphColor,
                '& svg': {
                  display: 'block',
                  flexShrink: 0,
                  width: '11px',
                  height: '11px',
                  minWidth: '11px',
                  minHeight: '11px'
                }
              }}
            >
              {isExpanded ? <IconMinus size={11} stroke={1.55} aria-hidden /> : <IconPlus size={11} stroke={1.55} aria-hidden />}
            </Box>
          </IconButton>
        ) : (
          <Box
            sx={{
              width: TREE_ICON_COL_WIDTH,
              height: TREE_ICON_COL_WIDTH,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: '-1px',
              bgcolor: 'transparent'
            }}
            aria-hidden
          >
            <CircleIcon
              sx={{
                color: theme.palette.mode === 'dark' ? '#757575' : theme.palette.bluegrey.dark,
                width: '8px'
              }}
            />
          </Box>
        )}
        <Typography
          onClick={onLabelClick}
          id={`org-dropdown-tree-item-typography-${row.idStr}`}
          className={`org-tree-item type-${item.type} ${hasChildren ? 'has-children' : 'leaf'} ${isLastOrgRow ? 'last-org-tree-row' : ''}`}
          data-type={item.type}
          data-has-children={hasChildren ? 'true' : 'false'}
          sx={{
            cursor: 'pointer',
            color: 'inherit',
            ...(item.type === 'All Organizations' ? { borderRadius: '8px' } : { borderRadius: isSelected ? '6px' : '' }),
            padding: '4px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            marginLeft: '0px',
            backgroundColor: selectedBg,
            ...(isExpanded && hasChildren && item.type !== 'All Organizations'
              ? { border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5, width: 'auto' }
              : {}),
            '&:hover': {
              background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.bluegrey.light,
              borderRadius: item.type === 'All Organizations' ? '8px' : '6px'
            },
            ...(isLastOrgRow && {
              position: 'relative',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 16,
                left: '-33px',
                height: 'calc(100vh - 380px)',
                width: '2px',
                zIndex: 1,
                bgcolor: 'background.paper'
              }
            })
          }}
        >
          {labelText}
        </Typography>
      </Box>
    </Box>
  );
});

/**
 * Windowed org / location hierarchy for large trees (same data shape as MUI TreeItem list).
 */
export default function VirtualOrgHierarchyTree({
  roots,
  expandedItems,
  toggleExpandedItem,
  handleNodeClick,
  calledFrom,
  selectedNodeId,
  selectedValue,
  selectedType,
  listId,
  className = 'org-hierarchy-dropdown-tree',
  reserveFooterPadding
}) {
  const scrollRef = useRef(null);
  const theme = useTheme();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedTypeStorage = useMemo(() => readSelectedTypeStorage(), [selectedNodeId, selectedValue, roots]);

  const expandedSet = useMemo(() => new Set((expandedItems || []).map((id) => String(id))), [expandedItems]);

  const { rows: flatRows, lastDepth1ChildId } = useMemo(() => {
    const raw = flattenVisibleOrgTree(roots, expandedSet);
    return recomputeVisibleConnectorFlags(raw);
  }, [roots, expandedSet]);

  const subtreeEndIndex = useMemo(() => computePreorderSubtreeEndIndex(flatRows), [flatRows]);

  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 16,
    // Index suffix: search can surface duplicate `id` values; virtualizer cache + React keys must stay unique.
    getItemKey: (index) => {
      const r = flatRows[index];
      return r ? `${r.idStr}-${index}` : index;
    }
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastOrgRowIndex = useMemo(() => {
    return flatRows.reduce((lastIndex, row, index) => {
      return row.item?.type === 'Org' ? index : lastIndex;
    }, -1);
  }, [flatRows]);
  return (
    <Box
      ref={scrollRef}
      id={listId}
      className={className}
      role="tree"
      aria-label="Organization hierarchy"
      sx={{
        minHeight: 10,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        margin: '0px',
        fontSize: 'calc(14px)',
        fontWeight: 400,
        fontFamily: '"Outfit", sans-serif',
        color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'rgb(66, 66, 66)',
        whiteSpace: 'pre',
        lineHeight: '16px',
        marginRight: 0.5,
        pl: theme.spacing(3),
        pr: theme.spacing(3),
        pb: reserveFooterPadding ? 0 : 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        bgcolor: 'background.paper',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { width: 6 }
      }}
    >
      <Box
        sx={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          flexShrink: 0,
          alignSelf: 'flex-start',
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }}
      >
        {virtualItems.map((virtualRow) => {
          const row = flatRows[virtualRow.index];
          if (!row) return null;
          const expanded = expandedSet.has(row.idStr);
          const isLastTreeRow = flatRows.length > 0 && virtualRow.index === flatRows.length - 1;
          const isLastOrgTreeRow = virtualRow.index === lastOrgRowIndex;
          return (
            <Box
              key={String(virtualRow.key ?? `${row.idStr}-${virtualRow.index}`)}
              data-index={virtualRow.index}
              data-org-tree-last-row={isLastTreeRow ? 'true' : undefined}
              role="presentation"
              className={isLastTreeRow ? 'org-tree-row-last' : undefined}
              sx={{
                position: 'absolute',
                top: `${Math.round(virtualRow.start)}px`,
                left: 0,
                width: '100%',
                height: ROW_HEIGHT,
                overflow: 'visible',
                bgcolor: 'transparent'
              }}
            >
              <VirtualOrgTreeRow
                row={row}
                isExpanded={expanded}
                toggleExpandedItem={toggleExpandedItem}
                handleNodeClick={handleNodeClick}
                calledFrom={calledFrom}
                selectedNodeId={selectedNodeId}
                selectedValue={selectedValue}
                selectedType={selectedType}
                selectedTypeStorage={selectedTypeStorage}
                isLastTreeRow={isLastTreeRow}
                isLastOrgTreeRow={isLastOrgTreeRow}
                prevRowDepth={virtualRow.index > 0 ? flatRows[virtualRow.index - 1]?.depth : undefined}
                flatRows={flatRows}
                lastDepth1ChildId={lastDepth1ChildId}
                subtreeEndIndex={subtreeEndIndex}
                flatRowIndex={virtualRow.index}
                flatRowCount={flatRows.length}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
